import {
	Column,
	Entity,
	Index,
	JoinColumn,
	ManyToOne,
	OneToMany,
} from 'typeorm';
import { BaseEntity } from './base.entity';
import { NamedLocationEntity } from './named-location.entity';
import { SalesRouteEntity } from './sales-route.entity';
import {
	SalesTripPlanEntity,
	type SalesTripStatus,
} from './sales-trip-plan.entity';
import type { SalesTripStopEntity } from './sales-trip-stop.entity';
import type { UserEntity } from './user.entity';

/**
 * One rep's plan for one calendar day (decision 1: Route → Trip → Stop). A
 * trip is built from a sales route or ad hoc; its stops carry planned times
 * and outcomes, and a completed stop CREATES a client visit (decision 2).
 *
 * Time model (decision 19, the tasks model): `tripDate` is a plain calendar
 * date in `timezone` (IANA); `plannedStart` / `plannedEnd` and every stop's
 * `plannedStart` are stored UTC and rendered in `timezone`.
 *
 * Ownership: `_owner_userId` is the planner (rep or leader); `salespersonUserId`
 * is who executes and is what `:self` scoping compares against.
 *
 * Start / end points: exactly one of the named-location / warehouse pair, or
 * both null = "current location"; a null end = same as start.
 */
@Entity('sales_trip')
@Index('ix_sales_trip_salesperson_date', ['salespersonUserId', 'tripDate'])
export class SalesTripEntity extends BaseEntity {
	@Column({ name: 'fk_user__id_salesperson', type: 'int', unsigned: true })
	public salespersonUserId!: number;

	@Column({
		name: 'fk_sales_route__id',
		type: 'int',
		unsigned: true,
		nullable: true,
	})
	public salesRouteId?: number | null;

	@Column({
		name: 'fk_sales_trip_plan__id',
		type: 'int',
		unsigned: true,
		nullable: true,
	})
	public salesTripPlanId?: number | null;

	/** Calendar date in `timezone`, YYYY-MM-DD. */
	@Column({ name: 'tripDate', type: 'date' })
	public tripDate!: string;

	@Column({ name: 'timezone', type: 'varchar', length: 64 })
	public timezone!: string;

	@Column({ name: 'name', type: 'varchar', length: 255, nullable: true })
	public name?: string | null;

	/** UTC — when the rep leaves the start location. */
	@Column({ name: 'plannedStart', type: 'datetime' })
	public plannedStart!: Date;

	@Column({ name: 'plannedEnd', type: 'datetime', nullable: true })
	public plannedEnd?: Date | null;

	@Column({
		name: 'fk_named_location__id_start',
		type: 'int',
		unsigned: true,
		nullable: true,
	})
	public namedLocationIdStart?: number | null;

	@Column({
		name: 'fk_warehouse__id_start',
		type: 'int',
		unsigned: true,
		nullable: true,
	})
	public warehouseIdStart?: number | null;

	@Column({
		name: 'fk_named_location__id_end',
		type: 'int',
		unsigned: true,
		nullable: true,
	})
	public namedLocationIdEnd?: number | null;

	@Column({
		name: 'fk_warehouse__id_end',
		type: 'int',
		unsigned: true,
		nullable: true,
	})
	public warehouseIdEnd?: number | null;

	@Column({ name: 'status', type: 'varchar', length: 20, default: "'draft'" })
	public status!: SalesTripStatus;

	/** Last computed total drive time for the day. */
	@Column({ name: 'estimatedDriveMinutes', type: 'int', nullable: true })
	public estimatedDriveMinutes?: number | null;

	@Column({ name: 'notes', type: 'text', nullable: true })
	public notes?: string | null;

	@ManyToOne('UserEntity', {
		nullable: true,
		createForeignKeyConstraints: false,
	})
	@JoinColumn([
		{ name: 'fk_user__id_salesperson', referencedColumnName: 'workdUserId' },
	])
	public salesperson?: UserEntity | null;

	@ManyToOne(() => SalesRouteEntity, { nullable: true })
	@JoinColumn({ name: 'fk_sales_route__id' })
	public fkSalesRoute?: SalesRouteEntity | null;

	@ManyToOne(() => NamedLocationEntity, { nullable: true })
	@JoinColumn({ name: 'fk_named_location__id_start' })
	public fkNamedLocationStart?: NamedLocationEntity | null;

	@ManyToOne(() => NamedLocationEntity, { nullable: true })
	@JoinColumn({ name: 'fk_named_location__id_end' })
	public fkNamedLocationEnd?: NamedLocationEntity | null;

	@ManyToOne(() => SalesTripPlanEntity, (plan) => plan.trips, {
		nullable: true,
	})
	@JoinColumn({ name: 'fk_sales_trip_plan__id' })
	public fkSalesTripPlan?: SalesTripPlanEntity | null;

	@OneToMany(
		'SalesTripStopEntity',
		(stop: SalesTripStopEntity) => stop.fkSalesTrip,
	)
	public stops?: Array<SalesTripStopEntity>;
}
